import { FC } from 'react';
import { NudosButton, NudosGenericModal } from '../../../../components/NudosComponents';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { IProductUpdateBlockedByQuotes } from '../../../../types/products';
import { Link } from 'react-router-dom';
import { IconWarning } from '../../../../assets/DesignSystem/SVGComponents';

import './UpdateBlockedByQuotesModal.scss';

interface IUpdateBlockedByQuotesModal {
  errorData: IProductUpdateBlockedByQuotes;
  countryNames: string[];
  actionVerb: string;
  forceButtonText: string;
  closeModal: () => void;
  onForce: () => void;
  isForcing?: boolean;
}

/**
 * Modal que advierte sobre cotizaciones activas que afectan el cambio de estado en tienda y
 * permite forzar el cambio de todas formas. Los textos de la pregunta y del botón de forzado
 * se adaptan al estado destino (prender / apagar / marcar para cotizar).
 *
 * @param errorData Datos del error 400 del backend, con los ids de cotizaciones afectadas.
 * @param countryNames Nombres de los países afectados.
 * @param actionVerb Verbo de la acción para la pregunta de confirmación (ej. "prender", "apagar").
 * @param forceButtonText Texto del botón que fuerza el cambio (ej. "Prender de todas formas").
 * @param closeModal Cierra el modal sin aplicar el cambio (botón "Cancelar").
 * @param onForce Se ejecuta al confirmar el forzado del cambio.
 * @param isForcing Indica si el forzado está en curso, para mostrar loading en el botón.
 */
const UpdateBlockedByQuotesModal: FC<IUpdateBlockedByQuotesModal> = ({
  errorData,
  countryNames,
  actionVerb,
  forceButtonText,
  closeModal,
  onForce,
  isForcing
}: IUpdateBlockedByQuotesModal) => {
  const countriesText = countryNames?.join(', ');

  return (
    <NudosGenericModal
      modalContent={
        <div id="updateBlockedByQuotesModal">
          <div className="iconContainer">
            <div className="iconBox">
              <IconWarning className="scaleWaningIcon" />
            </div>
          </div>
          <div className="errorMessage">Hay cotizaciones activas en {countriesText} para esta herramienta</div>
          <div className="quoteLinksContainer">
            {errorData?.affectedQuoteIds?.slice(0, 2)?.map(quoteId => (
              <Link
                key={quoteId}
                target="_blank"
                rel="noopener noreferrer"
                className="quoteLink"
                to={`/quote/details/${quoteId}`}
              >
                Q° {quoteId} - Ver cotización
                <BordOneToneIcon variant="externalLink" standardSize={12} stroke="#54A1FC" />
              </Link>
            ))}
            {errorData?.totalAffected > 2 && (
              <span className="remainingCount">Y {errorData?.totalAffected - 2} más</span>
            )}
          </div>
          <div className="bottomMessage">¿Seguro que deseas {actionVerb} la herramienta en {countriesText}?</div>
          <div className="buttonContainer">
            <NudosButton
              componentSize="small"
              colorPalette="secondary"
              customWidth="180px"
              customHeight="38px"
              buttonText="Cancelar"
              handleClick={closeModal}
              isButtonDisabled={isForcing}
            />
            <NudosButton
              componentSize="small"
              colorPalette="primary"
              customWidth="180px"
              customHeight="38px"
              buttonText={forceButtonText}
              handleClick={onForce}
              isButtonLoading={isForcing}
            />
          </div>
        </div>
      }
    />
  );
};

export default UpdateBlockedByQuotesModal;
